import moment from 'moment-timezone';

import {
  Village,
  VillageDeal,
  VillageFutureBooking,
  VillagePrice,
  VillagePricePeriod,
  VillagePriceVariants,
} from '@/services/types';

type Bedrooms = {
  single: number;
  double: number;
  flexible: number;
  twin: number;
  triple: number;
  quadruple: number;
};

type SelectedDates = {
  start: Date | null;
  end: Date | null;
};

type Props = {
  selectedDates?: SelectedDates;
  selectedPeriod?: VillagePricePeriod;
  priceVariant?: VillagePriceVariants;
  village?: Village;
};

type DisabledDate = {
  timestampStart: moment.Moment;
  timestampEnd: moment.Moment;
  type: string;
};

export default function useVillage({
  selectedDates,
  selectedPeriod,
  priceVariant,
  village,
}: Props) {
  const allPropsAreDefined =
    !!selectedDates && !!selectedPeriod && !!priceVariant && !!village;

  function momentWithTz(
    value?: moment.MomentInput,
    format?: moment.MomentFormatSpecification | undefined,
    strict?: boolean | undefined,
  ) {
    const timezone = 'Europe/Rome';
    if (value) {
      return moment(value, format, strict).tz(timezone);
    }

    return moment().tz(timezone);
  }

  function countNights(dates: SelectedDates) {
    return moment(dates.end).diff(dates.start, 'days');
  }

  function getMinNights({
    selectedDates,
    village,
    nightsCount,
  }: {
    selectedDates: SelectedDates;
    village: Village;
    nightsCount: number;
  }) {
    const minNights: number[] = [];
    const dayBeforeEndDate = momentWithTz(selectedDates.end).subtract(
      1,
      'days',
    );
    const startDate = momentWithTz(selectedDates.start);

    if (nightsCount > 0) {
      village.prices.forEach(({ period }) => {
        if (
          startDate.isBetween(period.startDate, period.endDate, 'day', '[]') ||
          dayBeforeEndDate.isBetween(
            period.startDate,
            period.endDate,
            'day',
            '[]',
          )
        ) {
          minNights.push(period.minNights);
        }
      });
    }

    return minNights.length > 0 ? Math.max(...minNights) : 1;
  }

  function isWeeklyBooking({
    selectedDates,
    village,
    nightsCount,
  }: {
    selectedDates: SelectedDates;
    village: Village;
    nightsCount: number;
  }) {
    let isWeeklyBooking = false;
    const dayBeforeEndDate = momentWithTz(selectedDates.end).subtract(
      1,
      'days',
    );

    if (nightsCount > 0) {
      village.prices.forEach(({ period }) => {
        const { startDate, endDate } = period;
        if (dayBeforeEndDate.isBetween(startDate, endDate, 'day', '[]')) {
          if (period.weeklyBooking) {
            isWeeklyBooking = true;
          }
        }
      });
    }
    return isWeeklyBooking;
  }

  function getNightsToCharge(
    isWeeklyBooking: boolean,
    daysCount: number,
    minNights: number,
    type?: 'optioned' | string,
  ) {
    if (type === 'optioned') {
      return daysCount;
    }

    if (isWeeklyBooking) {
      return Math.ceil(daysCount / 7) * 7;
    }

    return Math.max(daysCount, minNights);
  }

  function getDatePickerLastAvailableDate(
    priceVariants: VillagePriceVariants[],
  ) {
    let currentYear = momentWithTz().year();

    const availableYears = priceVariants
      .filter(
        ({ enabled, villaPricing }) =>
          villaPricing.year >= currentYear && enabled && villaPricing.enabled,
      )
      .map(({ villaPricing }) => villaPricing.year);

    const lastYear = Math.max(...availableYears);

    const lastAvailablePriceVariant = priceVariants.find(
      ({ villaPricing }) => villaPricing.year === lastYear,
    );

    if (lastAvailablePriceVariant) {
      const { villaPricing } = lastAvailablePriceVariant;

      const lastPeriod = villaPricing.pricePeriods.reduce((latest, current) => {
        return momentWithTz(current.endDate).isAfter(latest.endDate)
          ? current
          : latest;
      }, villaPricing.pricePeriods[0]);

      return lastPeriod.endDate;
    }
  }

  function smartBedroomSelect({
    countRooms,
    count,
  }: {
    countRooms: Bedrooms;
    count: number;
  }) {
    let leftCount = count;
    const totalSleeps = { value: 0 };
    const selectedQuantities: { [key: string]: number } = {
      single: 0,
      double: 0,
      flexible: 0,
      twin: 0,
      triple: 0,
      quadruple: 0,
    };

    const bedrooms: { [key: string]: { maxQuantity: number; sleeps: number } } =
      {
        single: { maxQuantity: countRooms.single, sleeps: 1 },
        double: { maxQuantity: countRooms.double, sleeps: 2 },
        twin: { maxQuantity: countRooms.twin, sleeps: 2 },
        triple: { maxQuantity: countRooms.triple, sleeps: 3 },
        quadruple: { maxQuantity: countRooms.quadruple, sleeps: 4 },
        flexible: { maxQuantity: countRooms.flexible, sleeps: 0 },
      };

    // In this way we fill first the biggest rooms
    const roomKeys = Object.keys(bedrooms).reverse();

    roomKeys.forEach((key) => {
      if (bedrooms[key].maxQuantity > 0 && leftCount > 0) {
        if (leftCount > bedrooms[key].maxQuantity) {
          selectedQuantities[key] = bedrooms[key].maxQuantity;
          leftCount -= bedrooms[key].maxQuantity;
        } else {
          selectedQuantities[key] = leftCount;
          leftCount = 0;
        }
        totalSleeps.value += selectedQuantities[key] * bedrooms[key].sleeps;
      }
    });

    if (selectedQuantities.flexible > 0) {
      selectedQuantities.double += selectedQuantities.flexible;
      selectedQuantities.flexible = 0;
    }

    selectedQuantities.double += leftCount; // The flexible rooms will be marked as double

    const filteredSelectedQuantities = Object.keys(selectedQuantities)
      .filter((key) => selectedQuantities[key] > 0)
      .reduce<{ [key: string]: number }>((obj, key) => {
        obj[key] = selectedQuantities[key];
        return obj;
      }, {});

    return filteredSelectedQuantities;
  }

  function getDailyVillagePrices(
    village: Village,
    selectedDates: SelectedDates,
    totalBedrooms: number,
    totalGuests: number,
    type?: string,
  ) {
    const dailyPrices = [];
    const nightsCount = countNights(selectedDates);
    const minNights = getMinNights({ selectedDates, village, nightsCount });
    const weeklyBooking =
      type === 'optioned'
        ? false
        : isWeeklyBooking({
            selectedDates,
            village,
            nightsCount,
          });

    const curDate = momentWithTz(selectedDates.start).startOf('day');

    const nightsToCharge = getNightsToCharge(
      weeklyBooking,
      nightsCount,
      minNights,
      type,
    );

    const chargeEndDate = momentWithTz(selectedDates.start).add(
      nightsToCharge,
      'd',
    );

    while (curDate.isBefore(chargeEndDate, 'day')) {
      const dailyPrice: { date: string; price: null | number } = {
        date: momentWithTz(curDate).format('D/M/YYYY'),
        price: null,
      };

      const villagePrice = findVillagePrice(
        village,
        curDate,
        totalBedrooms,
        totalGuests,
      );

      if (villagePrice) {
        if (
          dailyPrice.price &&
          parseFloat(villagePrice.originalPrice) < dailyPrice.price
        ) {
          dailyPrice.price = parseInt(villagePrice.originalPrice) / 7;
        }
        if (dailyPrice.price == null) {
          dailyPrice.price = parseInt(villagePrice.originalPrice) / 7;
        }
      }

      dailyPrices.push(dailyPrice);
      curDate.add(1, 'days');
    }

    return dailyPrices;
  }

  function getPricePeriod({
    priceVariant,
    selectedDates,
  }: {
    priceVariant: VillagePriceVariants;
    selectedDates: SelectedDates;
  }) {
    const period = priceVariant.villaPricing.pricePeriods.find((period) => {
      if (selectedDates.start && selectedDates.end) {
        const curDate = momentWithTz(selectedDates.start).startOf('day');

        return curDate.isBetween(period.startDate, period.endDate, 'day', '[]');
      }
    });
    return period;
  }

  function getPriceVariant({
    priceVariants,
    totalBedrooms,
    totalGuests,
    nextYear,
  }: {
    priceVariants: VillagePriceVariants[];
    totalBedrooms: number;
    totalGuests: number;
    nextYear?: boolean;
  }): VillagePriceVariants | null {
    let currentYear = momentWithTz().year();

    if (nextYear) {
      currentYear += 1;
    }

    const currentYearPricevariants = priceVariants.filter(
      (variant) => variant.villaPricing.year === currentYear && variant.enabled,
    );

    let curPriceVariant: VillagePriceVariants | null = null;

    currentYearPricevariants.forEach((variant) => {
      if (
        totalBedrooms <= variant.bedrooms &&
        totalGuests <= variant.sleeps &&
        (curPriceVariant === null || variant.sleeps < curPriceVariant.sleeps)
      ) {
        curPriceVariant = variant;
      }
    });

    return curPriceVariant;
  }

  function findVillagePrice(
    village: Village,
    date: moment.Moment,
    totalBedrooms: number,
    totalGuests: number,
  ) {
    const pricesForDate = village.prices?.filter((price) => {
      const pricePeriod = {
        startDate: moment(price.period.startDate.split('T')[0]),
        endDate: moment(price.period.endDate.split('T')[0]),
      };
      const priceVariant = price.variant;

      const isBetween = date.isBetween(
        pricePeriod.startDate,
        pricePeriod.endDate,
        'day',
        '[]',
      );

      const isMatchingVariant =
        totalBedrooms <= priceVariant.bedrooms &&
        totalGuests <= priceVariant.sleeps;

      return isBetween && isMatchingVariant;
    });

    if (!pricesForDate || pricesForDate.length === 0) {
      return null;
    }

    const bestPrice = pricesForDate.reduce((prev, current) => {
      const prevPrice = parseFloat(prev.originalPrice);
      const currentPrice = parseFloat(current.originalPrice);

      return currentPrice < prevPrice ? current : prev;
    });

    return bestPrice || null;
  }

  function getOriginalPrice(
    village: Village,
    selectedDates: SelectedDates,
    totalBedrooms: number,
    totalGuests: number,
    type?: string,
  ) {
    let villaPrice = 0;
    let invalidPrice = false;

    const dailyPrices = getDailyVillagePrices(
      village,
      selectedDates,
      totalBedrooms,
      totalGuests,
      type,
    );

    dailyPrices.forEach((dailyPrice) => {
      if (dailyPrice.price === null) {
        invalidPrice = true;
      } else {
        villaPrice += dailyPrice.price;
      }
    });

    if (invalidPrice) {
      return {
        originalPrice: 0,
        rawPrice: 0,
        dailyPrices,
      };
    }

    return {
      originalPrice: Math.round(villaPrice),
      // Unrounded sum of the nightly prices. The backend subtracts the deal discount
      // from this raw value and only rounds afterwards; rounding here first makes the
      // front total drift by 1 EUR. Keep both so each consumer picks the right one.
      rawPrice: villaPrice,
      dailyPrices,
    };
  }

  function getVillagePrice(
    // Raw nightly sum, not the rounded originalPrice. Mirror of
    // VillaBookingHandler::getVillaBookingPrice in vt-split: the discount is applied
    // to the raw sum and the result is rounded only at the end. Rounding before the
    // discount drifts the front total 1 EUR away from the backend order.
    rawPrice: number,
    villageDeals: VillageDeal[],
    selectedDates: SelectedDates,
    dailyPrices: {
      date: string;
      price: number | null;
    }[],
  ) {
    const villaPrice: { price: number; deal: VillageDeal | null } = {
      price: Math.round(rawPrice),
      deal: null,
    };
    const today = momentWithTz();
    const startDate = momentWithTz(selectedDates.start);
    const endDate = momentWithTz(selectedDates.end);

    const daysCount = endDate.diff(startDate, 'days');

    const visebleDeals = villageDeals.filter(({ enabled, deal }) => {
      const dealVisibleFrom = momentWithTz(deal.visibleFrom);
      const dealVisibleTo = momentWithTz(deal.visibleTo);

      const isVisible = today.isBetween(
        dealVisibleFrom,
        dealVisibleTo,
        'day',
        '[]',
      );

      return enabled && isVisible;
    });

    let bestVillaPrice = Number.MAX_SAFE_INTEGER;

    visebleDeals.forEach(({ deal, enabled, document }) => {
      let totalsToBeDiscounted = 0;
      let discountedNights = 0;
      let curVillaPrice = rawPrice;

      dailyPrices.forEach((dailyPrice) => {
        const date = momentWithTz(dailyPrice.date, 'D/M/YYYY');
        const dealStartsAt = momentWithTz(deal.startsAt.split('T')[0]);
        const dealEndsAt = momentWithTz(deal.endsAt.split('T')[0]);

        if (date.isBetween(dealStartsAt, dealEndsAt, 'day', '[]')) {
          totalsToBeDiscounted += dailyPrice.price ?? 0;
          discountedNights++;
        }
      });

      const dealCanBeAppliedAndReturned =
        discountedNights >= deal.minNights && daysCount >= deal.minNights;

      if (deal !== null && dealCanBeAppliedAndReturned) {
        if (deal.type === 'fixed') {
          curVillaPrice -= parseFloat(deal.value);
        } else {
          curVillaPrice -= totalsToBeDiscounted * (parseInt(deal.value) / 100);
        }
      }

      if (curVillaPrice < bestVillaPrice) {
        bestVillaPrice = curVillaPrice;
        villaPrice.price = Math.round(curVillaPrice);
        if (discountedNights > 0 && dealCanBeAppliedAndReturned) {
          villaPrice.deal = { enabled, deal, document };
        }
      }
    });

    return villaPrice;
  }

  function checkIfPeriodHasMinNights(
    dates: SelectedDates,
    period: VillagePricePeriod,
  ) {
    const periodMinNights = period.minNights;
    const nightsCount = countNights(dates);

    return nightsCount >= periodMinNights;
  }

  function checkIfCanCheckIn(
    date: Date,
    priceVariants: VillagePriceVariants[],
  ) {
    let canCheckIn = true;
    let checkInDays: string[] = [];
    let weekDay = new Date(date).getDay() - 1;
    const isSunday = weekDay === -1;

    if (isSunday) {
      weekDay = 6;
    }

    const weekDayString = weekDay.toString();

    const findedPeriod = findPeriodInVariants(priceVariants, date);

    if (findedPeriod) {
      checkInDays = findedPeriod.checkInDays;
      canCheckIn = checkInDays.some((checkIn) => checkIn === weekDayString);
    }

    return {
      canCheckIn,
      checkInDays,
    };
  }

  function getUnavailableDates(futureBookings: VillageFutureBooking[]) {
    const currentDate = new Date();

    const unavailableDates: { busy: DisabledDate[]; optioned: DisabledDate[] } =
      {
        busy: [],
        optioned: [],
      };

    let previousDate: Omit<DisabledDate, 'type'> | null = null;

    futureBookings?.map((booking) => {
      if (
        !['booked', 'not_available', 'cart', 'optioned'].includes(booking.type)
      ) {
        return;
      }

      const currentDate = {
        timestampStart: momentWithTz(booking.timestampStart),
        timestampEnd: momentWithTz(booking.timestampEnd).subtract(1, 'days'),
      };

      if (booking.type === 'cart') {
        currentDate.timestampStart = momentWithTz(
          booking.timestampStart,
        ).subtract(1, 'days');
      }

      if (previousDate) {
        const diff = currentDate.timestampStart.diff(
          previousDate.timestampEnd,
          'days',
        );
        // an unavailability is added between one booking and another
        // if there are not at least 4 days
        if (diff >= 1 && diff <= 4) {
          unavailableDates.busy.push({
            timestampStart: previousDate.timestampEnd,
            timestampEnd: currentDate.timestampStart,
            type: 'busy',
          });
        }
      }

      previousDate = { ...currentDate };
      if (booking.type === 'booked' || booking.type === 'not_available') {
        unavailableDates.busy.push({ ...currentDate, type: 'busy' });
      }

      if (booking.type === 'cart' || booking.type === 'optioned') {
        unavailableDates.optioned.push({ ...currentDate, type: 'optioned' });
      }
    });
    return unavailableDates;
  }

  function findPeriodInVariants(
    priceVariants: VillagePriceVariants[],
    date: Date,
  ) {
    let period: VillagePricePeriod | undefined;

    const formatedDate = new Date(date);
    const currentPriceVariants = priceVariants.filter(
      (variant) => variant.villaPricing.year === formatedDate.getFullYear(),
    );

    currentPriceVariants.forEach((variant) => {
      const findedPeriod = variant.villaPricing.pricePeriods.find((period) => {
        const startDate = new Date(period.startDate);
        const endDate = new Date(period.endDate);
        const startPeriodTime =
          startDate.getTime() + startDate.getTimezoneOffset() * 60000;
        const endPeriodTime =
          endDate.getTime() + endDate.getTimezoneOffset() * 60000;

        return (
          formatedDate.getTime() >= startPeriodTime &&
          formatedDate.getTime() <= endPeriodTime
        );
      });

      if (findedPeriod) {
        period = findedPeriod;
      }
    });

    if (period) {
      return period;
    }
  }

  function getSecurityDeposit(
    priceVariants: VillagePriceVariants[],
    totalBedrooms: number,
    totalGuests: number,
  ) {
    const currentDate = new Date();

    const currentYearPriceVariantsSecurityDeposits = priceVariants.filter(
      (variant) => {
        if (variant.villaPricing.year === currentDate.getFullYear()) {
          return variant;
        }
      },
    );

    let curPriceVariant: VillagePriceVariants | null = null;

    currentYearPriceVariantsSecurityDeposits.forEach((variant) => {
      if (
        totalBedrooms <= variant.bedrooms &&
        totalGuests <= variant.sleeps &&
        (curPriceVariant === null || variant.sleeps < curPriceVariant.sleeps)
      ) {
        curPriceVariant = variant;
      }
    });

    const securityDeposit =
      Number(
        (curPriceVariant as VillagePriceVariants | null)?.securityDeposit,
      ) ?? 0;

    return securityDeposit;
  }

  return {
    checkIfPeriodHasMinNights,
    checkIfCanCheckIn,
    getOriginalPrice,
    countNights,
    getNightsToCharge,
    getUnavailableDates,
    isWeeklyBooking,
    getVillagePrice,
    getPriceVariant,
    getPricePeriod,
    smartBedroomSelect,
    getDatePickerLastAvailableDate,
    getMinNights,
    getSecurityDeposit,
  };
}
